import {
  BackLink,
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useCoverPublishing } from "~/components/covers/use-cover-publishing";
import { EventForm } from "~/components/events/EventForm";
import { EventQuestions } from "~/components/events/EventQuestions";
import { EventRegistrations } from "~/components/events/EventRegistrations";
import { EventStatusPanel } from "~/components/events/EventStatusPanel";
import { Screen } from "~/components/ui/Screen";
import {
  ApiError,
  createEvent,
  deleteEvent,
  fetchAdminEvent,
  updateEvent,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { EventAdminCreate } from "~/types/events-admin";

function translateEventError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "eventEditor.errors.slugTaken";
    if (error.status === 422) return "eventEditor.errors.validation";
    return "common.somethingWentWrong";
  }
  return "eventEditor.errors.network";
}

/** One route for creating and editing calendar entries. */
export function EventEditorRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isCreate = id === undefined;
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const event = useQuery({
    queryKey: queryKeys.event(id ?? "new"),
    queryFn: ({ signal }) => fetchAdminEvent(id ?? "", signal),
    enabled: !isCreate,
  });

  // A save can put a library cover on (or take one off) the event, which changes that
  // cover's usage count, so the library is refreshed along with the lists.
  const invalidateLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.eventsRoot }),
      queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot }),
    ]);

  const create = useMutation({
    mutationFn: createEvent,
    onSuccess: async (created) => {
      await invalidateLists();
      // Straight into the editor rather than back to the list: the reminder ledger is
      // computed on save, and the first thing an editor wants after creating a webinar
      // is to see which legs were actually scheduled.
      void navigate(`/events/${created.id}`);
    },
    onError: (error) => setErrorKey(translateEventError(error)),
  });

  const update = useMutation({
    mutationFn: (body: EventAdminCreate) => updateEvent(id ?? "", body),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.event(updated.id), updated);
      await invalidateLists();
      setSavedOpen(true);
    },
    onError: (error) => setErrorKey(translateEventError(error)),
  });

  // The "Proběhlo" stamp is one request outside the form: ending a broadcast is an
  // act, not a draft to save, and it must not drag unsaved field edits along with it.
  const end = useMutation({
    mutationFn: (endedAt: string | null) =>
      updateEvent(id ?? "", { ended_at: endedAt }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.event(updated.id), updated);
      await invalidateLists();
    },
    onError: (error) => setErrorKey(translateEventError(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteEvent(id ?? ""),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: queryKeys.event(id ?? "") });
      await invalidateLists();
      void navigate("/events");
    },
    onError: (error) => {
      setConfirmingDelete(false);
      setErrorKey(translateEventError(error));
    },
  });

  // Publishes into the library only; the form holds the URL and the save commits it.
  const covers = useCoverPublishing(setErrorKey);

  const busy =
    create.isPending ||
    update.isPending ||
    remove.isPending ||
    end.isPending ||
    covers.busy;
  const submit = (body: EventAdminCreate) => {
    setErrorKey(null);
    if (isCreate) create.mutate(body);
    else update.mutate(body);
  };

  const header = (
    <Box sx={{ marginBottom: 3 }}>
      <BackLink
        to="/events"
        label={t("eventEditor.backToList")}
        testId="event.back"
      />
      <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1 }}>
        <WithHalfdot size={11}>
          {t(isCreate ? "eventEditor.createTitle" : "eventEditor.editTitle")}
        </WithHalfdot>
      </Typography>
    </Box>
  );

  if (!isCreate && event.isPending) {
    return (
      <Screen maxWidth="xl">
        {header}
        <LoadingState />
      </Screen>
    );
  }

  if (!isCreate && event.isError) {
    const notFound =
      event.error instanceof ApiError &&
      (event.error.status === 404 || event.error.status === 422);
    return (
      <Screen maxWidth="xl">
        {header}
        {notFound ? (
          <EmptyState message={t("eventEditor.notFound")} />
        ) : (
          <ErrorState onRetry={() => void event.refetch()} />
        )}
      </Screen>
    );
  }

  return (
    <Screen maxWidth="xl">
      {header}
      <EventForm
        key={event.data ? `${event.data.id}:${event.data.updated_at}` : "new"}
        initial={event.data}
        busy={busy}
        errorKey={errorKey}
        submitLabel={t(isCreate ? "eventEditor.create" : "eventEditor.save")}
        onSubmit={submit}
        covers={covers}
        aside={
          event.data ? (
            <EventStatusPanel
              event={event.data}
              busy={busy}
              onEnd={(endedAt) => end.mutate(endedAt)}
            />
          ) : null
        }
        actions={
          isCreate ? null : (
            <Button
              color="error"
              variant="text"
              disabled={busy}
              onClick={() => setConfirmingDelete(true)}
              data-testid="event.delete"
            >
              {t("eventEditor.delete")}
            </Button>
          )
        }
      />

      {event.data ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1fr) minmax(0, 1fr)",
            },
            gap: 3,
            marginTop: 4,
          }}
        >
          <EventQuestions eventId={event.data.id} state={event.data.state} />
          <EventRegistrations eventId={event.data.id} slug={event.data.slug} />
        </Box>
      ) : null}

      <Dialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      >
        <DialogTitle>{t("eventEditor.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("eventEditor.deleteConfirmBody", {
              title: event.data?.title ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => remove.mutate()}
            disabled={busy}
            data-testid="event.delete-confirm"
          >
            {t("eventEditor.deleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedOpen}
        autoHideDuration={3000}
        onClose={() => setSavedOpen(false)}
        message={t("eventEditor.saved")}
        data-testid="event.saved"
      />
    </Screen>
  );
}
